import { ServiceType, BookingType } from '../types';

export const API_URL = '/api/booking';
export const CALENDAR_API_URL = '/api/calendar';

export const fetchBookings = async (page = 1, search = '', sortBy = 'booking_date', sortOrder = 'desc'): Promise<any> => {
  try {
    const response = await fetch(`${API_URL}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        action: 'getBookings', 
        storeId: '372',
        page,
        search,
        sortBy,
        sortOrder
      })
    });

    if (!response.ok) {
      throw new Error('Network response was not ok');
    }

    const data = await response.json();
    
    if (!data.success) {
      throw new Error(data.message);
    }

    return data;
  } catch (error) {
    console.error('Error fetching bookings:', error);
    throw error;
  }
};

export const updateBookingStatus = async (bookingId: string, status: string): Promise<boolean> => {
  try {
    const response = await fetch(`${API_URL}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        action: 'updateBookingStatus',
        storeId: '372',
        bookingId,
        status
      })
    });

    if (!response.ok) {
      throw new Error('Failed to update booking status');
    }

    const data = await response.json();
    return data.success === true;
  } catch (error) {
    console.error('Error updating booking status:', error);
    throw error;
  }
};

export const deleteBooking = async (bookingId: string): Promise<boolean> => {
  try {
    const response = await fetch(`${API_URL}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        action: 'deleteBooking',
        storeId: '372',
        bookingId
      })
    });

    if (!response.ok) {
      throw new Error('Failed to delete booking');
    }

    const data = await response.json();
    return data.success === true;
  } catch (error) {
    console.error('Error deleting booking:', error);
    throw error;
  }
};

export const deleteCalendarEvent = async (eventId: string): Promise<boolean> => {
  try {
    const response = await fetch('/api/calendar/delete-event', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        eventId
      })
    });

    if (!response.ok) {
      throw new Error('Failed to delete calendar event');
    }

    const data = await response.json();
    return data.success === true;
  } catch (error) {
    console.error('Error deleting calendar event:', error);
    throw error;
  }
};

export const updateSettings = async (settings: Record<string, any>): Promise<boolean> => {
  try {
    const response = await fetch(`${API_URL}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        action: 'updateBookingSettings',
        storeId: '372',
        settings
      })
    });

    if (!response.ok) {
      throw new Error('Failed to update settings');
    }

    const data = await response.json();
    return data.success === true;
  } catch (error) {
    console.error('Error updating settings:', error);
    throw error;
  }
};

export const fetchBookingSettings = async (): Promise<any> => {
  try {
    const response = await fetch(`${API_URL}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        action: 'getBookingSettings',
        storeId: '372'
      })
    });

    if (!response.ok) {
      throw new Error('Failed to fetch booking settings');
    }

    const data = await response.json();
    if (!data.success) {
      throw new Error(data.message || 'Failed to fetch booking settings');
    }

    return data.settings;
  } catch (error) {
    console.error('Error fetching booking settings:', error);
    throw error;
  }
};

export const fetchPublicSettings = async (): Promise<any> => {
  try {
    const response = await fetch(`${API_URL}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        action: 'getBookingSettings',
        storeId: '372'
      })
    });

    if (!response.ok) {
      throw new Error('Failed to fetch public settings');
    }

    const data = await response.json();
    if (!data.success) {
      throw new Error(data.message || 'Failed to fetch public settings');
    }

    return {
      maintenance_mode: Boolean(data.settings?.maintenance_mode),
      maintenance_message: data.settings?.maintenance_message || 'We are currently not available for booking, please check back later.',
      calendar_status: Boolean(data.settings?.calendar_status),
      max_advance_booking_days: Number(data.settings?.max_advance_booking_days ?? 90),
      time_slot_duration: Number(data.settings?.time_slot_duration ?? 60),
      business_hours_start: data.settings?.business_hours_start || '09:00:00',
      business_hours_end: data.settings?.business_hours_end || '18:00:00',
      working_days: data.settings?.working_days ? data.settings.working_days.split(',').map(Number) : [1,2,3,4,5,6],
      allow_same_day_booking: Boolean(data.settings?.allow_same_day_booking)
    };
  } catch (error) {
    console.error('Error fetching public settings:', error);
    return {
      maintenance_mode: false,
      maintenance_message: 'We are currently not available for booking, please check back later.',
      calendar_status: false,
      max_advance_booking_days: 90,
      time_slot_duration: 60,
      business_hours_start: '09:00:00',
      business_hours_end: '18:00:00',
      working_days: [1,2,3,4,5,6],
      allow_same_day_booking: false
    };
  }
};

export const fetchServiceDetails = async (serviceId: number): Promise<ServiceType | null> => {
  try {
    const response = await fetch(`${API_URL}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        action: 'serviceDetail',
        storeId: '372',
        serviceId
      })
    });

    if (!response.ok) {
      throw new Error('Failed to fetch service details');
    }

    const data = await response.json();
    
    if (!data.success || !data.service) {
      return null;
    }

    return {
      id: data.service.id,
      name: data.service.name,
      description: data.service.description || '',
      duration: `${data.service.hours_min}-${data.service.hours_max} hours`,
      price: parseFloat(data.service.amount),
      image: data.service.image,
      category_name: data.service.category_name
    };
  } catch (error) {
    console.error('Error fetching service details:', error);
    return null;
  }
};

export const fetchBookedTimeSlots = async (date: string) => {
  try {
    const formattedDate = new Date(date).toISOString().split('T')[0];
    console.log('API: Fetching booked slots for', formattedDate);
    
    const response = await fetch('/api/booking', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        action: 'booked_slots',
        date: formattedDate
      })
    });
    
    console.log('API: Response status', response.status);
    
    if (!response.ok) {
      throw new Error('Failed to fetch booked time slots');
    }

    const data = await response.json();
    console.log('API: Response data', data);
    
    if (data.success) {
      return {
        booked_hours: data.booked_hours || [],
        is_all_day_booked: data.is_all_day_booked || false
      };
    } else {
      throw new Error(data.message || 'Failed to fetch booked time slots');
    }
  } catch (error) {
    console.error('Error fetching booked time slots:', error);
    return { booked_hours: [], is_all_day_booked: false };
  }
};

export const fetchFullyBookedDates = async (startDate: string, endDate: string): Promise<string[]> => {
  try {
    const response = await fetch(`${API_URL}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        action: 'getFullyBookedDates',
        storeId: '372',
        startDate,
        endDate
      })
    });

    if (!response.ok) {
      throw new Error('Failed to fetch fully booked dates');
    }

    const data = await response.json();
    if (data.success) {
      return data.booked_dates || [];
    }
    return [];
  } catch (error) {
    console.error('Error fetching fully booked dates:', error);
    return [];
  }
};

export const fetchAvailabilityPeriods = async (): Promise<any[]> => {
  try {
    const response = await fetch(`${API_URL}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        action: 'getAvailabilityPeriods',
        storeId: '372'
      })
    });

    if (!response.ok) {
      throw new Error('Failed to fetch availability periods');
    }

    const data = await response.json();
    if (data.success) {
      return data.periods || [];
    }
    return [];
  } catch (error) {
    console.error('Error fetching availability periods:', error);
    return [];
  }
};

export const addAvailabilityPeriod = async (start_date: string, end_date: string, status: string, label?: string): Promise<boolean> => {
  try {
    const response = await fetch(`${API_URL}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        action: 'addAvailabilityPeriod',
        storeId: '372',
        start_date,
        end_date,
        status,
        label
      })
    });

    if (!response.ok) {
      throw new Error('Failed to add availability period');
    }

    const data = await response.json();
    return data.success === true;
  } catch (error) {
    console.error('Error adding availability period:', error);
    throw error;
  }
};

export const updateAvailabilityPeriod = async (periodId: number, updates: any): Promise<boolean> => {
  try {
    const response = await fetch(`${API_URL}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        action: 'updateAvailabilityPeriod',
        storeId: '372',
        periodId,
        ...updates
      })
    });

    if (!response.ok) {
      throw new Error('Failed to update availability period');
    }

    const data = await response.json();
    return data.success === true;
  } catch (error) {
    console.error('Error updating availability period:', error);
    throw error;
  }
};

export const deleteAvailabilityPeriod = async (periodId: number): Promise<boolean> => {
  try {
    const response = await fetch(`${API_URL}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        action: 'deleteAvailabilityPeriod',
        storeId: '372',
        periodId
      })
    });

    if (!response.ok) {
      throw new Error('Failed to delete availability period');
    }

    const data = await response.json();
    return data.success === true;
  } catch (error) {
    console.error('Error deleting availability period:', error);
    throw error;
  }
};

export const syncCalendar = async (): Promise<boolean> => {
  try {
    const response = await fetch(`${CALENDAR_API_URL}/sync`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error('Failed to sync calendar');
    }

    const data = await response.json();
    return data.success === true;
  } catch (error) {
    console.error('Error syncing calendar:', error);
    throw error;
  }
};

export const submitBooking = async (formData: FormData): Promise<unknown> => {
  try {
    const receiptFile = formData.get('receipt') as File;
    const base64Receipt = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(receiptFile);
    });

    const payload = {
      action: 'submitBooking',
      storeId: formData.get('storeId'),
      serviceId: formData.get('serviceId'),
      bookingDate: formData.get('bookingDate'),
      startTime: formData.get('startTime'),
      customerName: formData.get('customerName'),
      customerEmail: formData.get('customerEmail'),
      customerPhone: formData.get('customerPhone'),
      partySize: formData.get('partySize'),
      notes: formData.get('notes'),
      receipt: base64Receipt
    };

    const response = await fetch(`${API_URL}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      throw new Error('Failed to submit booking');
    }

    const data = await response.json();
    
    if (!data.success) {
      throw new Error(data.message);
    }

    return data;
  } catch (error) {
    console.error('Error submitting booking:', error);
    throw error;
  }
};

export const submitBookingWithoutReceipt = async (bookingData: {
  serviceId: string;
  bookingDate: string;
  startTime: string;
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  partySize?: string;
  notes?: string;
}): Promise<unknown> => {
  try {
    const payload = {
      action: 'submitBooking',
      storeId: '372',
      ...bookingData
    };

    const response = await fetch(`${API_URL}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      throw new Error('Failed to submit booking');
    }

    const data = await response.json();
    
    if (!data.success) {
      throw new Error(data.message);
    }

    return data;
  } catch (error) {
    console.error('Error submitting booking:', error);
    throw error;
  }
};

export const fetchServices = async (): Promise<ServiceType[]> => {
  try {
    const response = await fetch(`${API_URL}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        action: 'getGalleryItems',
        storeId: '372'
      })
    });

    if (!response.ok) {
      throw new Error('Failed to fetch services');
    }

    const data = await response.json();
    
    if (!data.success) {
      throw new Error(data.message);
    }

    const items = Array.isArray(data.items) ? data.items : [];
    
    return items.map((item: unknown) => ({
      id: item.id,
      name: item.name,
      description: item.description || '',
      duration: `${item.hours_min}-${item.hours_max} hours`,
      price: parseFloat(item.amount),
      image: item.image,
      category_id: item.category_id,
      category_name: item.category_name
    }));
  } catch (error) {
    console.error('Error fetching services:', error);
    return [];
  }
};

export const fetchCategories = async () => {
  try {
    const response = await fetch(`${API_URL}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        action: 'getCategories',
        storeId: '372'
      })
    });

    if (!response.ok) {
      throw new Error('Failed to fetch categories');
    }

    const data = await response.json();
    
    if (!data.success) {
      throw new Error(data.message);
    }

    return Array.isArray(data.categories) ? data.categories : [];
  } catch (error) {
    console.error('Error fetching categories:', error);
    return [];
  }
};

export const createBooking = async (formData: FormData): Promise<unknown> => {
  try {
    const response = await fetch(`${API_URL}`, {
      method: 'POST',
      body: formData
    });

    if (!response.ok) {
      throw new Error('Failed to create booking');
    }

    const data = await response.json();
    
    if (!data.success) {
      throw new Error(data.message);
    }

    return data;
  } catch (error) {
    console.error('Error creating booking:', error);
    throw error;
  }
};
